import React, { useState, useCallback } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { object as yupObject, string } from 'yup';
import { ROUTES } from 'config/Nav';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { useRouter } from 'next/router';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import * as IconMUI from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { reportError } from '../../lib/errors';
import useStyles from './styles';
import { useAuth } from '@elevator/ui';
import { confirmEmailVerification, User, ActionAreaContainer } from '@elevator/ui';
import { PageHeader, SectionHeader } from 'molecules';
import { useVerifyEmailModal } from './VerifyEmailModalContext';
import { useInitialVerificationCode } from '../../hooks/useInitialVerificationCode';
import { useResendVerificationCode } from '../../hooks/useResendVerificationCode';

const FADE_TIMEOUT = 1000;
const SUCCESS_HEADER_TITLE = 'Email Verified';
const SUCCESS_HEADER_DESCRIPTION =
  'Thank you for verifying your email. You can now access your account.';
const BUTTON_TEXT = 'MY ACCOUNT';
const SENDING_TEXT = 'Sending...';
const RESEND_VERIFICATION_CODE_TEXT = 'Resend Verification Code';
const VERIFY_EMAIL_TEXT = 'Verify Email';
const VERIFICATION_CODE_LABEL = 'Verification Code';

function getResendButtonText(isResending: boolean, cooldownSeconds: number): string {
  if (isResending) return SENDING_TEXT;
  if (cooldownSeconds > 0) return `Resend Code (${cooldownSeconds}s)`;
  return RESEND_VERIFICATION_CODE_TEXT;
}

interface VerifyEmailFormValues {
  verificationCode: string;
}

export interface VerifyEmailModalProps {
  /** When true, show success state (for Storybook). */
  defaultShowSuccess?: boolean;
}

/**
 * Modal that displays the verify-email flow. Opens when the user is logged in
 * and has not verified their email, or when openModal() is called from context
 * (e.g. "Verify Email" in Account Settings). Contains form (code, resend,
 * verify) and success view (message + My Account button).
 */
const VerifyEmailModal = ({ defaultShowSuccess = false }: VerifyEmailModalProps = {}) => {
  const { classes } = useStyles();
  const router = useRouter();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, hydrateUser } = useAuth();
  const { open: contextOpen, setOpen: setContextOpen } = useVerifyEmailModal();

  const [verified, setVerified] = useState(defaultShowSuccess);
  useInitialVerificationCode(currentUser);
  const { isResending, cooldownSeconds, handleResend } = useResendVerificationCode(
    currentUser ?? ({ username: '' } as User),
  );

  const open = (!!currentUser && !currentUser.emailVerified) || contextOpen;

  const handleClose = useCallback(() => {
    setContextOpen(false);
  }, [setContextOpen]);

  const validationSchema = yupObject({
    verificationCode: string().required(
      'A verification code is required to verify your email',
    ),
  });

  const initialValues: VerifyEmailFormValues = {
    verificationCode: '',
  };

  const handleSubmit = useCallback(
    async (
      values: VerifyEmailFormValues,
      actions: FormikHelpers<VerifyEmailFormValues>,
    ) => {
      if (!currentUser) return;
      try {
        await confirmEmailVerification(
          values.verificationCode,
          process.env.AUTH_ENDPOINT,
        );
        actions.setSubmitting(false);
        setVerified(true);
      } catch (err) {
        actions.setSubmitting(false);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to verify email. Please try again.';
        enqueueSnackbar(errorMessage, {
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          variant: 'error',
          preventDuplicate: true,
        });
        reportError(err, {
          metaData: { operation: 'cognito confirmEmailVerification' },
          user: { username: currentUser.username },
        });
      }
    },
    [currentUser, enqueueSnackbar],
  );

  const goToAccount = useCallback(async () => {
    try {
      await hydrateUser(false);
      setContextOpen(false);
      await router.push(ROUTES.ACCOUNT.ACCOUNT_SETTINGS.to);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to navigate to account settings. Please try again.';
      enqueueSnackbar(errorMessage, {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        variant: 'error',
        preventDuplicate: true,
      });
      reportError(err, { metaData: { operation: 'VerifyEmailModal goToAccount' } });
    }
  }, [hydrateUser, setContextOpen, router, enqueueSnackbar]);

  if (!currentUser || currentUser.emailVerified) {
    if (!contextOpen) return null;
  }

  const headerTitle = verified ? SUCCESS_HEADER_TITLE : 'Verify Your Email';
  const headerDescription = verified
    ? SUCCESS_HEADER_DESCRIPTION
    : currentUser
      ? `Please verify your email (${currentUser.email}). You will not be able to recover your password without verifying your email.`
      : 'You must be signed in to verify your email.';

  const renderBody = () => {
    if (!currentUser) return null;

    if (verified) {
      return (
        <Fade in timeout={FADE_TIMEOUT}>
          <Box paddingTop={3} display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="medium"
              color="primary"
              fullWidth={mobile}
              onClick={goToAccount}
              aria-label="Go to account settings"
            >
              {BUTTON_TEXT}
            </Button>
          </Box>
        </Fade>
      );
    }

    return (
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit: formikSubmit,
          isValid,
          isSubmitting,
          handleChange,
        }) => (
          <Box className={classes.contentRoot}>
            <form onSubmit={(e) => e.preventDefault()}>
              <TextField
                variant="standard"
                margin="normal"
                required
                fullWidth
                name="verificationCode"
                label={VERIFICATION_CODE_LABEL}
                id="verificationCode"
                value={values.verificationCode}
                onChange={handleChange}
                error={touched.verificationCode && Boolean(errors.verificationCode)}
                helperText={touched.verificationCode ? errors.verificationCode : ''}
                aria-label={VERIFICATION_CODE_LABEL}
              />
              <ActionAreaContainer direction={mobile ? 'column' : 'row'}>
                <Button
                  size="medium"
                  fullWidth={mobile}
                  variant="outlined"
                  color="primary"
                  onClick={handleResend}
                  disabled={isResending || cooldownSeconds > 0}
                  startIcon={<IconMUI.Refresh />}
                  aria-label={getResendButtonText(isResending, cooldownSeconds)}
                >
                  {getResendButtonText(isResending, cooldownSeconds)}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="medium"
                  fullWidth={mobile}
                  color="primary"
                  onClick={() => formikSubmit()}
                  disabled={!isValid || isSubmitting}
                  aria-label={VERIFY_EMAIL_TEXT}
                >
                  {VERIFY_EMAIL_TEXT}
                </Button>
              </ActionAreaContainer>
            </form>
          </Box>
        )}
      </Formik>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      data-component="VerifyEmailModal"
      aria-label="Verify email"
      aria-describedby="Verify your email account"
      slotProps={{
        root: { className: classes.modalRoot },
        backdrop: { className: classes.modalBackdrop },
      }}
    >
      <Paper square elevation={0} className={classes.card}>
        <Box className={classes.closeButtonContainer}>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            size="small"
            className={classes.closeButton}
          >
            <IconMUI.Close />
          </IconButton>
        </Box>
        {verified ? (
          <SectionHeader
            title={headerTitle}
            description={headerDescription}
            divider={false}
            icon={
              <IconMUI.Check sx={{ color: 'success.main', fontSize: 32 }} aria-hidden />
            }
          />
        ) : (
          <PageHeader
            title={headerTitle}
            divider={false}
            description={headerDescription}
          />
        )}
        {renderBody()}
      </Paper>
    </Modal>
  );
};

export default VerifyEmailModal;
